/**
 * Utility functions for handling multi-application functionality
 */

/**
 * Get all user applications from localStorage
 * @returns {Array} Array of user application objects
 */
export const getUserApplications = () => {
  try {
    const apps = localStorage.getItem('user_applications');
    return apps ? JSON.parse(apps) : [];
  } catch (error) {
    console.error('Error parsing user applications:', error);
    return [];
  }
};

/**
 * Get box-to-app mapping from localStorage
 * @returns {Object} Mapping of box_id to app_id
 */
export const getBoxToAppMap = () => {
  try {
    const mapping = localStorage.getItem('box_to_app_map');
    return mapping ? JSON.parse(mapping) : {};
  } catch (error) {
    console.error('Error parsing box to app mapping:', error);
    return {};
  }
};

/**
 * Set box-to-app mapping in localStorage
 * @param {Object} mapping - Mapping of box_id to app_id
 */
export const setBoxToAppMap = (mapping) => {
  try {
    localStorage.setItem('box_to_app_map', JSON.stringify(mapping));
  } catch (error) {
    console.error('Error storing box to app mapping:', error);
  }
};

/**
 * Get the app_id for a specific box_id
 * @param {number} boxId - The box ID
 * @returns {string} The app_id for this box (defaults to "default")
 */
export const getAppIdForBox = (boxId) => {
  const mapping = getBoxToAppMap();
  // Use String() to ensure consistent key lookup (handles both int and string IDs)
  const appId = mapping[String(boxId)] || 'default';
  
  // Only warn if mapping is populated but box not found, and it's not an expected mismatch
  // (e.g., checking numeric box ID against string venue IDs during app switching is expected)
  if (process.env.NODE_ENV === 'development' && appId === 'default' && boxId) {
    const mappingKeys = Object.keys(mapping);
    const hasMapping = mappingKeys.length > 0;
    
    if (hasMapping) {
      // Check if this is an expected mismatch (different ID formats)
      const boxIdIsNumber = typeof boxId === 'number' || (!isNaN(boxId) && !String(boxId).includes('/'));
      const mappingHasStringIds = mappingKeys.some(key => key.includes('/') || isNaN(key));
      const mappingHasNumberIds = mappingKeys.some(key => !key.includes('/') && !isNaN(key));
      
      // Only warn if ID formats match (both numeric or both string), meaning it's a real missing box
      // If formats don't match, it's likely an app switch transition which is expected
      if ((boxIdIsNumber && mappingHasNumberIds) || (!boxIdIsNumber && mappingHasStringIds)) {
        console.warn('⚠️ Box not found in mapping:', boxId, 'Available keys:', mappingKeys);
      }
    }
  }
  return appId;
};

/**
 * Create box-to-app mapping from boxes data
 * @param {Array} boxes - Array of box objects with app_id
 * @returns {Object} Mapping of box_id to app_id
 */
export const createBoxToAppMapping = (boxes) => {
  const mapping = {};
  boxes.forEach(box => {
    if (box.id && box.app_id) {
      // Use String() to ensure consistent key format (handles both int and string IDs)
      mapping[String(box.id)] = box.app_id;
    } else if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ Box missing id or app_id:', box);
    }
  });
  if (process.env.NODE_ENV === 'development') {
    console.log('🗺️ Created box-to-app mapping:', mapping);
  }
  return mapping;
};

/**
 * Get user applications grouped by app_id
 * @returns {Object} Object with app_id as keys and application data as values
 */
export const getUserApplicationsMap = () => {
  const apps = getUserApplications();
  const appsMap = {};
  apps.forEach(app => {
    if (app.app_id) {
      appsMap[app.app_id] = app;
    }
  });
  return appsMap;
};

/**
 * Get the JWT token for a specific app_id
 * @param {string} appId - The application ID
 * @returns {string|null} The JWT token for this app, or null if not found
 */
export const getJwtForApp = (appId) => {
  const appsMap = getUserApplicationsMap();
  const app = appsMap[appId];
  return app ? app.jwt_token : null;
};

/**
 * Get application info for a specific app_id
 * @param {string} appId - The application ID
 * @returns {Object|null} The application data, or null if not found
 */
export const getApplicationInfo = (appId) => {
  const appsMap = getUserApplicationsMap();
  return appsMap[appId] || null;
};

/**
 * Get the icon path for a specific app_id
 * @param {string} appId - The application ID
 * @returns {string|null} The icon path for this app, or null if not found
 */
export const getApplicationIcon = (appId) => {
  const app = getApplicationInfo(appId);
  return app ? app.icon_path : null;
};

/**
 * Clear all multi-app data from localStorage
 */
export const clearMultiAppData = () => {
  localStorage.removeItem('user_applications');
  localStorage.removeItem('box_to_app_map');
};

/**
 * Check if user has multiple applications
 * @returns {boolean} True if user has more than one application
 */
export const hasMultipleApplications = () => {
  const apps = getUserApplications();
  return apps.length > 1;
};

/**
 * Get a display name for an application
 * @param {string} appId - The application name/ID
 * @returns {string} A user-friendly name for the application
 */
export const getApplicationDisplayName = (appId) => {
  // Return the app_id as-is for proper application names
  if (appId === 'default') {
    return 'Arbox Default';
  } else if (appId === 'ROUND2') {
    return 'ROUND2';
  } else if (appId === 'lockerroomtlv') {
    return 'Lockerroom TLV';
  } else if (appId && appId.length > 0) {
    return appId;
  } else {
    return 'Unknown App';
  }
};

/**
 * Debug function to log current multi-app state
 */
export const debugMultiAppState = () => {
  console.log('=== Multi-App Debug Info ===');
  console.log('User Applications:', getUserApplications());
  console.log('Box to App Mapping:', getBoxToAppMap());
  console.log('Has Multiple Apps:', hasMultipleApplications());
  console.log('Applications Map:', getUserApplicationsMap());
  console.log('============================');
};
